"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Clipboard, Code2, Download, Printer, Share2 } from "lucide-react";
import type { AnonymousFormat } from "@qrody/api-client/render";
import type { GeneratorCapabilities } from "../domain/capabilities";
import { SaveAction } from "../actions/save-action";
import type { SaveInputValue } from "../domain/save-mapping";
import type { QrGeneratorActionState } from "../components/qr-generator";

export interface ActionBarProps {
  caps: GeneratorCapabilities;
  format: AnonymousFormat;
  scanImpossibleWithoutPro: boolean;
  saveInput: SaveInputValue | null;
  liveValidationOk: boolean;
  hasRendered: boolean;
  pending: boolean;
  canShareFiles: boolean;
  mounted: boolean;
  hideInlineActions: boolean;
  actionSlot?: ((state: QrGeneratorActionState) => ReactNode) | undefined;
  actionState: QrGeneratorActionState;
  onDownload: () => void;
  onCopyImage: () => void;
  onCopyHtml: () => void;
  onShare: () => void;
  onPrint: () => void;
}

/**
 * Inline action bar under the file-resolution slider — Download (primary,
 * or caller-supplied actionSlot in edit-mode) + secondary Copy/Share/Print
 * pills. Entirely behind the `mounted` hydration gate (uses navigator APIs).
 */
export function ActionBar(props: ActionBarProps): ReactNode {
  const t = useTranslations("generator");
  const {
    caps,
    format,
    scanImpossibleWithoutPro,
    saveInput,
    liveValidationOk,
    hasRendered,
    pending,
    canShareFiles,
    mounted,
    hideInlineActions,
    actionSlot,
    actionState,
    onDownload,
    onCopyImage,
    onCopyHtml,
    onShare,
    onPrint,
  } = props;

  if (hideInlineActions) return null;
  if (!mounted) return null;

  if (actionSlot) {
    return (
      <div className="flex flex-wrap items-center gap-2 pt-1">{actionSlot(actionState)}</div>
    );
  }

  const downloadDisabled = pending || scanImpossibleWithoutPro;
  const downloadTitle = scanImpossibleWithoutPro
    ? t("scanWarning.downloadBlocked")
    : t("action.download");

  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      {caps.primaryAction === "save" && saveInput ? (
        <SaveAction
          payload={saveInput.payload}
          design={saveInput.design}
          options={saveInput.options}
          disabled={downloadDisabled || !liveValidationOk}
        />
      ) : (
        <button
          type="button"
          onClick={onDownload}
          disabled={downloadDisabled || (caps.primaryAction === "save" && !saveInput)}
          title={downloadTitle}
          aria-label={t("action.download")}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          {t("action.download")}
        </button>
      )}
      <button
        type="button"
        onClick={onCopyImage}
        disabled={!hasRendered || format !== "png"}
        className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        title={format !== "png" ? t("action.copyImageFailed") : t("action.copyImageTooltip")}
        aria-label={t("action.copyImage")}
      >
        <Clipboard className="h-3.5 w-3.5" aria-hidden />
        {t("action.copyImage")}
      </button>
      <button
        type="button"
        onClick={onCopyHtml}
        disabled={!hasRendered}
        className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        title={t("action.copyHtmlTooltip")}
        aria-label={t("action.copyHtml")}
      >
        <Code2 className="h-3.5 w-3.5" aria-hidden />
        {t("action.copyHtml")}
      </button>
      {canShareFiles && (
        <button
          type="button"
          onClick={onShare}
          disabled={!hasRendered}
          className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          title={t("action.shareTooltip")}
          aria-label={t("action.share")}
        >
          <Share2 className="h-3.5 w-3.5" aria-hidden />
          {t("action.share")}
        </button>
      )}
      <button
        type="button"
        onClick={onPrint}
        disabled={!hasRendered}
        className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        title={t("action.printTooltip")}
        aria-label={t("action.print")}
      >
        <Printer className="h-3.5 w-3.5" aria-hidden />
        {t("action.print")}
      </button>
    </div>
  );
}
